/**
 * HeadhuntPanel — Track B headhunt sprint UI
 *
 * On mount: GET /headhunt/status → if a sprint has already run, show history state
 * Step 1: GET /headhunt/brief  → display auto-generated Search Brief
 * Step 2: Recruiter reviews / edits brief fields inline
 * Step 3: POST /headhunt/launch (with any edits as overrides)
 * Step 4: Show result (candidates found / added)
 *
 * Used on: ProjectDashboard
 */

import { useState, useEffect, useCallback } from 'react'
import { Search, Loader2, ChevronDown, ChevronUp, RefreshCw, Trash2, RotateCcw } from 'lucide-react'
import { WaveIcon } from '@/components/common/WaveIcon'
import type {
  HeadhuntSearchBrief,
  HeadhuntLaunchResponse,
  HeadhuntStatusResponse,
  RerankSourcedResponse,
} from '@/lib/contracts'
import { engineEndpoints } from '@/lib/contracts'
import { apiRequest, engineApiClient } from '@/lib/apiClient'
// apiRequest signature: ({ path, method, body }) => Promise<T>

// ─── Job title variant expansion ────────────────────────────────────────────────────
//
// Given a project title string (possibly comma-separated), return a deduplicated
// list of the original values plus common label variants (e.g. “VP Sales” →
// “VP of Sales”, “Vice President Sales”, “Vice President of Sales”).

function expandTitleVariants(title: string): string[] {
  const variants = new Set<string>()

  for (const raw of title.split(',').map((t) => t.trim()).filter(Boolean)) {
    variants.add(raw)

    // VP X → VP of X, Vice President X, Vice President of X
    const vpMatch = raw.match(/^VP\s+(?!of\s)(.+)$/i)
    if (vpMatch) {
      variants.add(`VP of ${vpMatch[1]}`)
      variants.add(`Vice President ${vpMatch[1]}`)
      variants.add(`Vice President of ${vpMatch[1]}`)
    }

    // VP of X → VP X, Vice President X, Vice President of X
    const vpOfMatch = raw.match(/^VP of\s+(.+)$/i)
    if (vpOfMatch) {
      variants.add(`VP ${vpOfMatch[1]}`)
      variants.add(`Vice President ${vpOfMatch[1]}`)
      variants.add(`Vice President of ${vpOfMatch[1]}`)
    }

    // Vice President X → VP X, VP of X
    const viceMatch = raw.match(/^Vice President\s+(?!of\s)(.+)$/i)
    if (viceMatch) {
      variants.add(`VP ${viceMatch[1]}`)
      variants.add(`VP of ${viceMatch[1]}`)
    }

    // Director X → Director of X (and reverse)
    const dirMatch = raw.match(/^Director\s+(?!of\s)(.+)$/i)
    if (dirMatch) variants.add(`Director of ${dirMatch[1]}`)
    const dirOfMatch = raw.match(/^Director of\s+(.+)$/i)
    if (dirOfMatch) variants.add(`Director ${dirOfMatch[1]}`)

    // Head X → Head of X (and reverse)
    const headMatch = raw.match(/^Head\s+(?!of\s)(.+)$/i)
    if (headMatch) variants.add(`Head of ${headMatch[1]}`)
    const headOfMatch = raw.match(/^Head of\s+(.+)$/i)
    if (headOfMatch) variants.add(`Head ${headOfMatch[1]}`)
  }

  return Array.from(variants)
}

// ─── Types ────────────────────────────────────────────────────────────────────

type SprintState = 'checking' | 'idle' | 'loading_brief' | 'reviewing' | 'launching' | 'complete' | 'error' | 'previously_run'

interface HeadhuntPanelProps {
  projectId:          string
  onCandidatesAdded?: (count: number) => void
  onCandidatesCleared?: () => void
}

// ─── Clear Sourced Confirmation Modal ─────────────────────────────────────────

function ClearSourcedModal({
  projectId,
  onConfirmed,
  onCancel,
}: {
  projectId:   string
  onConfirmed: (deleted: number) => void
  onCancel:    () => void
}) {
  const [clearing, setClearing] = useState(false)
  const [error,    setError]    = useState<string | null>(null)

  const handleConfirm = async () => {
    setClearing(true)
    setError(null)
    try {
      const result = await apiRequest<{ projectId: string; deleted: number }>({
        path:   engineEndpoints.headhuntClearSourced(projectId),
        method: 'DELETE',
      })
      onConfirmed(result.deleted)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Clear failed — check Railway logs')
      setClearing(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#02182B] border border-red-500/30 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-red-500/10 rounded-lg flex items-center justify-center shrink-0">
            <Trash2 size={16} className="text-red-400" />
          </div>
          <div>
            <h2 className="text-white font-semibold text-sm">Clear sourced candidates?</h2>
            <p className="text-gray-400 text-xs mt-0.5">This cannot be undone</p>
          </div>
        </div>

        <p className="text-gray-300 text-sm mb-2">
          This will permanently delete all <span className="text-white font-medium">sourced</span> candidates from this project and reset the sprint counter.
        </p>
        <p className="text-gray-500 text-xs mb-5">
          Uploaded, applied, and referred candidates will not be affected.
        </p>

        {error && (
          <p className="text-red-400 text-xs mb-4 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleConfirm}
            disabled={clearing}
            className="flex-1 flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors"
          >
            {clearing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            {clearing ? 'Clearing…' : 'Yes, clear sourced'}
          </button>
          <button
            onClick={onCancel}
            disabled={clearing}
            className="px-4 py-2.5 bg-[#233D4C] border border-[#2a4a5c] text-gray-300 text-sm rounded-lg hover:text-white transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Editable tag list ────────────────────────────────────────────────────────

function TagList({
  label,
  items,
  onUpdate,
  autoFilled = false,
}: {
  label:      string
  items:      string[]
  onUpdate:   (updated: string[]) => void
  autoFilled?: boolean
}) {
  const [input, setInput] = useState('')

  const add = () => {
    const v = input.trim()
    if (v && !items.includes(v)) {
      onUpdate([...items, v])
    }
    setInput('')
  }

  const remove = (item: string) => onUpdate(items.filter((i) => i !== item))

  return (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-400 mb-1.5">{label}</label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {items.map((item) => (
          <span
            key={item}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#1a3347] border border-[#2a4a5c] rounded-full text-xs text-white"
          >
            {item}
            <button
              onClick={() => remove(item)}
              className="text-gray-500 hover:text-red-400 transition-colors ml-0.5"
            >
              ×
            </button>
          </span>
        ))}
        {items.length === 0 && (
          <span className="text-xs text-gray-500 italic">
            {label.toLowerCase().includes('location') ? 'No filter — broad search' : 'None set'}
          </span>
        )}
      </div>
      {autoFilled && (
        <p className="text-xs text-gray-600 mb-1.5">Auto-filled from intake</p>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder={`Add ${label.toLowerCase()}…`}
          className="flex-1 bg-[#02182B] border border-[#2a4a5c] rounded-lg px-3 py-1.5 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-[#FD802E]/50"
        />
        <button
          onClick={add}
          className="px-3 py-1.5 bg-[#233D4C] text-white text-xs rounded-lg border border-[#2a4a5c] hover:bg-[#2a4a5c] transition-colors"
        >
          Add
        </button>
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function HeadhuntPanel({ projectId, onCandidatesAdded, onCandidatesCleared }: HeadhuntPanelProps) {
  const [state,        setState]        = useState<SprintState>('checking')
  const [brief,        setBrief]        = useState<HeadhuntSearchBrief | null>(null)
  const [result,       setResult]       = useState<HeadhuntLaunchResponse | null>(null)
  const [error,        setError]        = useState<string | null>(null)
  const [open,         setOpen]         = useState(false)
  const [previousRun,  setPreviousRun]  = useState<HeadhuntStatusResponse | null>(null)
  const [showClearModal, setShowClearModal] = useState(false)
  const [reranking,      setReranking]      = useState(false)
  const [rerankMsg,      setRerankMsg]      = useState<string | null>(null)

  // Editable brief fields (overrides)
  const [titles,        setTitles]        = useState<string[]>([])
  const [seniorities,   setSeniorities]   = useState<string[]>([])
  const [locations,     setLocations]     = useState<string[]>([])
  const [keywords,      setKeywords]      = useState<string[]>([])
  const [mustHaveTerms, setMustHaveTerms] = useState<string[]>([])

  // Tracks which fields were auto-populated from intake/project data
  const [autoFilled, setAutoFilled] = useState<Set<string>>(new Set())

  // Auto-populate brief fields from intake + project when the review panel opens.
  // Fires once when state transitions to 'reviewing' (after loadBrief() succeeds).
  // Non-fatal: if this fetch fails the user can still edit fields manually.
  useEffect(() => {
    if (state !== 'reviewing') return

    let cancelled = false
    ;(async () => {
      try {
        const [project, thread] = await Promise.all([
          engineApiClient.fetchProject(projectId),
          engineApiClient.getIntakeThread(projectId),
        ])
        if (cancelled) return

        const willFill: string[] = []

        // Titles: expand project.title into common label variants and merge with
        // whatever the brief API already returned.
        if (project.title) {
          const variants = expandTitleVariants(project.title)
          setTitles((prev) => {
            const next = [...new Set([...prev, ...variants])]
            return next
          })
          willFill.push('titles')
        }

        // Location: pre-fill from regionFocus (or scan highlights for geo signals)
        // only when the brief returned no locations.
        const regionLoc = project.regionFocus?.trim()
        if (regionLoc) {
          setLocations((prev) => (prev.length > 0 ? prev : [regionLoc]))
          willFill.push('locations')
        } else {
          // Only add actual geographic strings as locations — never full must-have sentences
          // Use word-boundary matching to avoid "uk" matching inside words like "bulk" or "work"
          const geoOnlyTerms = [
            { key: 'united kingdom', val: 'United Kingdom' },
            { key: '\\buk\\b', val: 'United Kingdom' },
            { key: 'london', val: 'London, England, United Kingdom' },
            { key: 'united states', val: 'United States' },
            { key: '\\bus\\b', val: 'United States' },
            { key: 'new york', val: 'New York, United States' },
            { key: 'san francisco', val: 'San Francisco, United States' },
            { key: 'europe', val: 'Europe' },
            { key: 'remote', val: 'Remote' },
          ]
          // Only scan regionFocus and project title for geo — never scan mustHaves sentences
          const geoSource = [project.title ?? '', project.regionFocus ?? ''].join(' ').toLowerCase()
          const geoMatch = geoOnlyTerms.find(t => new RegExp(t.key, 'i').test(geoSource))
          if (geoMatch) {
            setLocations((prev) => (prev.length > 0 ? prev : [geoMatch.val]))
            willFill.push('locations')
          }
        }

        // Keywords: use first 3–4 mustHaves (short phrases) when keywords are empty.
        const kwFromMustHaves = thread.highlights.mustHaves
          .slice(0, 4)
          .map((mh) => mh.split(' ').slice(0, 4).join(' ').trim())
          .filter(Boolean)
        if (kwFromMustHaves.length > 0) {
          setKeywords((prev) => (prev.length > 0 ? prev : kwFromMustHaves))
          willFill.push('keywords')
        }

        // Must-Have Terms: full list from intake for boolean search display
        const allMustHaves = [
          ...thread.highlights.mustHaves,
          ...thread.highlights.leadershipSignals,
        ].slice(0, 12)
        setMustHaveTerms(allMustHaves)

        if (willFill.length > 0) setAutoFilled(new Set(willFill))
      } catch {
        // Non-fatal — brief is still usable without auto-populate
      }
    })()

    return () => { cancelled = true }
  }, [state, projectId])

  // Check sprint history on mount — show "previously run" state if a sprint has already happened
  useEffect(() => {
    apiRequest<HeadhuntStatusResponse>({
      path:   engineEndpoints.headhuntStatus(projectId),
      method: 'GET',
    })
      .then((status) => {
        if (status.hasRun) {
          setPreviousRun(status)
          setState('previously_run')
        } else {
          setState('idle')
        }
      })
      .catch(() => setState('idle')) // on error, fall through to idle — don't block the UI
  }, [projectId])

  const handleRerank = async () => {
    setReranking(true)
    setRerankMsg(null)
    try {
      const res = await apiRequest<RerankSourcedResponse>({
        path:   engineEndpoints.headhuntRerank(projectId),
        method: 'POST',
      })
      setRerankMsg(`Re-scored ${res.reranked} candidates with latest scoring`)
      onCandidatesCleared?.()   // trigger a candidate list refresh in the parent
    } catch {
      setRerankMsg('Re-score failed — check logs')
    } finally {
      setReranking(false)
    }
  }

  const loadBrief = useCallback(async () => {
    // Clear any stale error immediately — don't show a failure banner while loading a new brief
    setError(null)
    setState('loading_brief')
    try {
      const res = await apiRequest<{ brief: HeadhuntSearchBrief }>({
        path:   engineEndpoints.headhuntBrief(projectId),
        method: 'GET',
      })
      setBrief(res.brief)
      setTitles(res.brief.personTitles)
      setSeniorities(res.brief.personSeniorities)
      setLocations(res.brief.personLocations)
      setKeywords(res.brief.keywords)
      setAutoFilled(new Set()) // reset on each brief load
      setState('reviewing')
      setOpen(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load search brief')
      setState('error')
    }
  }, [projectId])

  const launchSprint = async () => {
    setState('launching')
    setError(null)
    try {
      const overrides = {
        ...(titles.join(',')      !== brief?.personTitles.join(',')      && { personTitles:      titles }),
        ...(seniorities.join(',') !== brief?.personSeniorities.join(',') && { personSeniorities: seniorities }),
        ...(locations.join(',')   !== brief?.personLocations.join(',')   && { personLocations:   locations }),
        ...(keywords.join(',')    !== brief?.keywords.join(',')          && { keywords }),
      }

      const res = await apiRequest<HeadhuntLaunchResponse>({
        path:   engineEndpoints.headhuntLaunch(projectId),
        method: 'POST',
        body:   Object.keys(overrides).length > 0 ? { overrides } : {},
      })

      // Handle non-fatal status codes that come back as 422 with a status field
      if (res.status === 'no_apollo_key') {
        setError('Talent database API key not configured. Add APOLLO_API_KEY to Railway environment variables.')
        setState('error')
        return
      }
      if (res.status === 'no_candidates') {
        setError(res.error || 'No matching candidates found. Try broadening the job titles or removing the location filter.')
        setState('error')
        return
      }
      if (res.status === 'failed') {
        setError(res.error || 'Headhunt sprint failed. Check Railway logs for details.')
        setState('error')
        return
      }

      setResult(res)
      setState('complete')
      if (res.candidatesAdded > 0) {
        onCandidatesAdded?.(res.candidatesAdded)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Headhunt sprint failed')
      setState('error')
    }
  }

  // ── Checking status on mount ─────────────────────────────────────────────────
  if (state === 'checking') {
    return (
      <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-xl p-5 mb-6 flex items-center gap-3">
        <Loader2 size={14} className="text-gray-500 animate-spin shrink-0" />
        <span className="text-gray-500 text-xs">Loading headhunt status…</span>
      </div>
    )
  }

  // ── Previously run — persistent history state ─────────────────────────────
  if (state === 'previously_run' && previousRun) {
    const lastRun = previousRun.lastRunAt
      ? new Date(previousRun.lastRunAt).toLocaleDateString('en-GB', {
          day: 'numeric', month: 'short', year: 'numeric',
        })
      : null

    return (
      <>
        <div className="bg-[#233D4C] border border-[#FD802E]/20 rounded-xl p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#FD802E]/10 rounded-lg flex items-center justify-center shrink-0">
              <WaveIcon size="sm" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">Headhunt</h3>
              <p className="text-gray-400 text-xs mt-0.5">
                Last sprint{lastRun ? ` · ${lastRun}` : ''} · <span className="text-[#FD802E]">{previousRun.candidatesAdded} sourced candidates added</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={loadBrief}
              className="flex items-center gap-2 bg-[#1a3347] border border-[#2a4a5c] text-white text-xs font-medium px-4 py-2 rounded-lg hover:bg-[#2a4a5c] transition-colors"
            >
              <RefreshCw size={12} /> Run again
            </button>
            <button
              onClick={handleRerank}
              disabled={reranking}
              className="flex items-center gap-1.5 text-gray-600 hover:text-[#FD802E] text-xs px-2 py-2 rounded-lg hover:bg-[#FD802E]/10 transition-colors disabled:opacity-40"
              title="Re-score existing candidates with latest scoring prompt"
            >
              {reranking ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
            </button>
            <button
              onClick={() => setShowClearModal(true)}
              className="flex items-center gap-1.5 text-gray-600 hover:text-red-400 text-xs px-2 py-2 rounded-lg hover:bg-red-500/10 transition-colors"
              title="Clear sourced candidates"
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>
        {rerankMsg && (
          <p className="text-xs text-[#FD802E] mt-2 px-1">{rerankMsg}</p>
        )}
        {showClearModal && (
          <ClearSourcedModal
            projectId={projectId}
            onConfirmed={(deleted) => {
              setShowClearModal(false)
              setState('idle')
              setPreviousRun(null)
              setResult(null)
              onCandidatesCleared?.()
              console.log(`[headhunt] cleared ${deleted} sourced candidates`)
            }}
            onCancel={() => setShowClearModal(false)}
          />
        )}
      </>
    )
  }

  // ── Idle state: Headhunt button ──────────────────────────────────────────────
  if (state === 'idle') {
    return (
      <div className="bg-[#233D4C] border border-[#FD802E]/20 rounded-xl p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#FD802E]/10 rounded-lg flex items-center justify-center shrink-0">
            <WaveIcon size="sm" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">Headhunt</h3>
            <p className="text-gray-400 text-xs mt-0.5">
              Erica searches our talent database for matching candidates, scores them, and adds the top 50 to your pipeline.
            </p>
          </div>
        </div>
        <button
          onClick={loadBrief}
          className="shrink-0 bg-[#FD802E] text-[#02182B] font-semibold px-5 py-2.5 rounded-lg hover:bg-[#ff8f45] transition-colors text-sm flex items-center gap-2"
        >
          <Search size={14} /> Start Headhunt
        </button>
      </div>
    )
  }

  // ── Loading brief ────────────────────────────────────────────────────────────
  if (state === 'loading_brief') {
    return (
      <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-xl p-5 mb-6 flex items-center gap-3">
        <WaveIcon size="sm" animate={true} />
        <span className="text-gray-300 text-sm">Building Search Brief from role context…</span>
      </div>
    )
  }

  // ── Launching ────────────────────────────────────────────────────────────────
  if (state === 'launching') {
    return (
      <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-xl p-5 mb-6 flex items-center gap-3">
        <WaveIcon size="md" animate={true} />
        <div>
          <p className="text-white text-sm font-medium">Headhunt in progress</p>
          <p className="text-gray-400 text-xs mt-0.5">Erica is querying the talent database, ranking candidates, and adding the top 50 to your pipeline.</p>
        </div>
      </div>
    )
  }

  // ── Complete ─────────────────────────────────────────────────────────────────
  if (state === 'complete' && result) {
    const skipped = result.candidatesSkipped ?? (result.candidatesFound - result.candidatesAdded)
    const allDuplicate = result.resultVariety === 'all_duplicate'
    const borderColor = allDuplicate ? 'border-yellow-500/20' : 'border-green-500/20'
    const iconColor   = allDuplicate ? 'text-yellow-400' : 'text-green-400'
    const iconBg      = allDuplicate ? 'bg-yellow-500/10' : 'bg-green-500/10'

    return (
      <>
      <div className={`bg-[#233D4C] border ${borderColor} rounded-xl p-5 mb-6 flex items-start justify-between gap-4`}>
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 ${iconBg} rounded-lg flex items-center justify-center shrink-0`}>
            <WaveIcon size="sm" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">
              {allDuplicate ? 'Headhunt complete — no new candidates' : 'Headhunt complete'}
            </h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
              <span className="text-xs text-gray-400">
                <span className="text-white font-medium">{result.candidatesFound}</span> candidates found
                {result.apolloPage && result.apolloPage > 1 && (
                  <span className="text-gray-600 ml-1">(page {result.apolloPage})</span>
                )}
              </span>
              <span className="text-gray-600 text-xs">·</span>
              <span className="text-xs text-gray-400">
                <span className={`font-medium ${allDuplicate ? 'text-yellow-400' : 'text-green-400'}`}>{result.candidatesAdded}</span> newly added
              </span>
              {skipped > 0 && (
                <>
                  <span className="text-gray-600 text-xs">·</span>
                  <span className="text-xs text-gray-500">{skipped} already in project</span>
                </>
              )}
            </div>
            {allDuplicate && (
              <p className="text-xs text-yellow-600 mt-1.5">
                The search returned the same result set. Try editing the Search Brief — add different titles, remove the location filter, or run again to fetch the next page.
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={handleRerank}
            disabled={reranking}
            className="flex items-center gap-1.5 text-gray-600 hover:text-[#FD802E] text-xs px-2 py-2 rounded-lg hover:bg-[#FD802E]/10 transition-colors disabled:opacity-40"
            title="Re-score candidates with latest scoring prompt"
          >
            {reranking ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
          </button>
          <button
            onClick={() => setShowClearModal(true)}
            className="flex items-center gap-1.5 text-gray-600 hover:text-red-400 text-xs px-2 py-2 rounded-lg hover:bg-red-500/10 transition-colors"
            title="Clear sourced candidates"
          >
            <Trash2 size={12} />
          </button>
          <button
            onClick={() => { setState('idle'); setResult(null) }}
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors px-2 py-2"
          >
            Run again
          </button>
        </div>
      </div>
      {rerankMsg && <p className="text-xs text-[#FD802E] mt-2 px-1">{rerankMsg}</p>}
      {showClearModal && (
        <ClearSourcedModal
          projectId={projectId}
          onConfirmed={(deleted) => {
            setShowClearModal(false)
            setState('idle')
            setPreviousRun(null)
            setResult(null)
            onCandidatesCleared?.()
            console.log(`[headhunt] cleared ${deleted} sourced candidates`)
          }}
          onCancel={() => setShowClearModal(false)}
        />
      )}
    </>
    )
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (state === 'error') {
    return (
      <div className="bg-[#233D4C] border border-red-500/20 rounded-xl p-5 mb-6 flex items-center justify-between gap-4">
        <div>
          <p className="text-white text-sm font-medium">Headhunt failed</p>
          <p className="text-gray-400 text-xs mt-0.5">{error}</p>
        </div>
        <button
          onClick={() => setState('idle')}
          className="shrink-0 text-xs text-[#FD802E] hover:text-orange-300 transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  // ── Reviewing brief ──────────────────────────────────────────────────────────
  return (
    <div className="bg-[#233D4C] border border-[#FD802E]/30 rounded-xl mb-6 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#FD802E]/10 rounded-lg flex items-center justify-center shrink-0">
            <WaveIcon size="sm" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">Search Brief</h3>
            <p className="text-gray-400 text-xs mt-0.5">
              {brief?.intakeSummary}
            </p>
          </div>
        </div>
        <button onClick={() => setOpen((o) => !o)} className="text-gray-400 hover:text-white transition-colors">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Editable brief */}
      {open && (
        <div className="border-t border-[#2a4a5c] px-5 py-4 space-y-1">
          <p className="text-xs text-gray-500 mb-4">
            Review and edit the search parameters. The talent database will be queried with these values.
            Changes here override the auto-generated brief for this sprint only.
          </p>

          <TagList label="Job Titles" items={titles} onUpdate={setTitles} autoFilled={autoFilled.has('titles')} />
          <TagList label="Seniority Levels" items={seniorities} onUpdate={setSeniorities} />
          <TagList label="Locations (empty = no filter)" items={locations} onUpdate={setLocations} autoFilled={autoFilled.has('locations')} />
          <TagList label="Keywords" items={keywords} onUpdate={setKeywords} autoFilled={autoFilled.has('keywords')} />

          {/* Must-Haves / Boolean Search Terms from intake */}
          {mustHaveTerms.length > 0 && (
            <div className="pt-2">
              <p className="text-xs text-gray-500 uppercase font-medium mb-2">Must-Haves from Intake</p>
              <p className="text-xs text-gray-600 mb-2">These criteria from Erica's intake are used to rank candidates. They are not Apollo search filters but inform scoring.</p>
              <div className="flex flex-wrap gap-1.5">
                {mustHaveTerms.map((term, i) => (
                  <span key={i} className="px-2.5 py-1 bg-[#FD802E]/10 text-[#FD802E] border border-[#FD802E]/20 rounded-full text-xs font-medium">
                    {term}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-3">
            <button
              onClick={launchSprint}
              className="flex-1 bg-[#FD802E] text-[#02182B] font-semibold px-5 py-2.5 rounded-lg hover:bg-[#ff8f45] transition-colors text-sm flex items-center justify-center gap-2"
            >
              <WaveIcon size="sm" animate={true} /> Launch Headhunt
            </button>
            <button
              onClick={() => setState('idle')}
              className="px-4 py-2.5 bg-[#1a3347] border border-[#2a4a5c] text-gray-400 text-sm rounded-lg hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
